import React, { useEffect, useState } from "react";
import { useEditor } from "@craftjs/core";
import "../../styles/components/editor/LayersPanel.css";

interface LayerComponentProps {
  id: string;
  name: string;
  depth: number;
  isSelected: boolean;
  isHovered: boolean;
  hasChildren: boolean;
  isExpanded: boolean;
  hidden?: boolean;
  onSelect: (id: string) => void;
  onHover: (id: string | null) => void;
  onToggleExpand: (id: string) => void;
  onToggleVisibility: (id: string) => void;
  onDelete?: (id: string) => void;
  isDeletable?: boolean;
}

const LayerComponent: React.FC<LayerComponentProps> = ({
  id,
  name,
  depth,
  isSelected,
  isHovered,
  hasChildren,
  isExpanded,
  hidden = false,
  onSelect,
  onHover,
  onToggleExpand,
  onToggleVisibility,
  onDelete,
  isDeletable,
}) => {
  return (
    <div
      className={`layer-item ${isSelected ? "selected" : ""} ${
        isHovered ? "hovered" : ""
      } ${hidden ? "hidden" : ""}`}
      style={{ paddingLeft: `${depth * 16}px` }}
      onClick={() => onSelect(id)}
      onMouseEnter={() => onHover(id)}
      onMouseLeave={() => onHover(null)}
    >
      {hasChildren && (
        <span
          className={`layer-toggle ${isExpanded ? "expanded" : ""}`}
          onClick={(e) => {
            e.stopPropagation();
            onToggleExpand(id);
          }}
        >
          {isExpanded ? "▼" : "►"}
        </span>
      )}
      <span className="layer-name">{name}</span>
      <div className="layer-actions">
        <button
          className={`layer-visibility-toggle ${hidden ? "hidden" : ""}`}
          onClick={(e) => {
            e.stopPropagation();
            onToggleVisibility(id);
          }}
          title={hidden ? "Show component" : "Hide component"}
        >
          {hidden ? "👁️‍🗨️" : "👁️"}
        </button>
        {isDeletable && onDelete && (
          <button
            className="layer-delete-btn"
            onClick={(e) => {
              e.stopPropagation();
              if (
                window.confirm(
                  "Are you sure you want to delete this component?"
                )
              ) {
                onDelete(id);
              }
            }}
            title="Delete component"
          >
            🗑️
          </button>
        )}
      </div>
    </div>
  );
};

export const LayersPanel: React.FC = () => {
  const { selected, actions, query } = useEditor((state, query) => {
    const currentNodeId = state.events.selected;
    let selected;

    if (currentNodeId) {
      // Handle Set object properly - extract the first item from the set
      // This fixes the [object Set] warning
      const nodeId =
        currentNodeId instanceof Set
          ? currentNodeId.size > 0
            ? Array.from(currentNodeId)[0]
            : null
          : typeof currentNodeId === "string" ||
            typeof currentNodeId === "number"
          ? String(currentNodeId)
          : null;

      // Only proceed if we have a valid nodeId
      if (nodeId && state.nodes[nodeId]) {
        selected = {
          id: nodeId,
          name: state.nodes[nodeId].data.name,
          displayName:
            state.nodes[nodeId].data.custom?.displayName ||
            state.nodes[nodeId].data.displayName,
          hidden: state.nodes[nodeId].data.hidden,
          settings:
            state.nodes[nodeId].related && state.nodes[nodeId].related.settings,
          isDeletable: query.node(nodeId).isDeletable(),
          dom: query.node(nodeId).get().dom,
          data: state.nodes[nodeId].data,
        };
      }
    }

    return {
      selected,
      nodes: state.nodes,
    };
  });

  const [expandedNodes, setExpandedNodes] = React.useState<
    Record<string, boolean>
  >({});
  const [hoveredNode, setHoveredNode] = React.useState<string | null>(null);
  const [showComponentTools, setShowComponentTools] = useState(false);

  // For debugging - log selected nodes
  useEffect(() => {
    if (selected) {
      console.log("Layer Panel - Selected component:", selected.id);
    }
  }, [selected]);

  // Get the root Canvas node
  const rootNodeId = Object.keys(query.getNodes()).find(
    (id) => query.node(id).get().data.parent === null
  );

  const handleToggleVisibility = (id: string) => {
    const node = query.node(id).get();
    const isCurrentlyHidden = node.data.hidden || false;

    actions.setProp(id, (props: any) => {
      props.hidden = !isCurrentlyHidden;
    });
  };

  const renderLayer = (id: string, depth: number = 0) => {
    if (!id) return null;

    const node = query.node(id).get();
    if (!node) return null;

    const isCanvas = node.data.custom?.isCanvas || node.data.nodes?.length > 0;
    const children = node.data.nodes || [];
    const isExpanded = expandedNodes[id] !== false; // Default to expanded
    const isDeletable = query.node(id).isDeletable();
    const isHidden = node.data.hidden || false;

    const nodeComponent = (
      <LayerComponent
        key={id}
        id={id}
        name={
          node.data.custom?.displayName ||
          node.data.displayName ||
          node.data.name ||
          node.data.type
        }
        depth={depth}
        isSelected={query.getEvent("selected").contains(id)}
        isHovered={hoveredNode === id}
        hasChildren={isCanvas && children.length > 0}
        isExpanded={isExpanded}
        hidden={isHidden}
        isDeletable={isDeletable}
        onSelect={(id) => actions.selectNode(id)}
        onHover={setHoveredNode}
        onToggleExpand={(id) => {
          setExpandedNodes((prev) => ({
            ...prev,
            [id]: !prev[id],
          }));
        }}
        onToggleVisibility={handleToggleVisibility}
        onDelete={(id) => actions.delete(id)}
      />
    );

    // If node is a Canvas and has children, render them recursively if expanded
    if (isCanvas && children.length > 0 && isExpanded) {
      return (
        <React.Fragment key={id}>
          {nodeComponent}
          {children.map((childId) => renderLayer(childId, depth + 1))}
        </React.Fragment>
      );
    }

    return nodeComponent;
  };

  // Component tools section
  const renderComponentTools = () => {
    if (!selected) return null;

    return (
      <div className="component-quick-tools">
        <h4>Quick Actions</h4>
        <div className="tool-buttons">
          <button
            className="tool-btn"
            onClick={() => {
              const json = query.node(selected.id).toNodeTree();
              localStorage.setItem("clipboard-component", JSON.stringify(json));
              alert("Component copied to clipboard!");
            }}
            title="Copy component"
          >
            📋 Copy
          </button>

          <button
            className="tool-btn"
            onClick={() => {
              const json = query.node(selected.id).toNodeTree();
              actions.addNodeTree(json, selected.id);
            }}
            title="Duplicate component"
          >
            🔄 Duplicate
          </button>

          <button
            className={`tool-btn ${selected.hidden ? "active" : ""}`}
            onClick={() => handleToggleVisibility(selected.id)}
            title={selected.hidden ? "Show component" : "Hide component"}
          >
            {selected.hidden ? "👁️‍🗨️ Show" : "👁️ Hide"}
          </button>

          {selected.isDeletable && (
            <button
              className="tool-btn delete"
              onClick={() => {
                if (
                  window.confirm(
                    "Are you sure you want to delete this component?"
                  )
                ) {
                  actions.delete(selected.id);
                }
              }}
              title="Delete component"
            >
              🗑️ Delete
            </button>
          )}
        </div>
      </div>
    );
  };

  return (
    <div className="layers-panel">
      <div className="panel-header">
        <h3>Layers</h3>
        {selected && (
          <button
            className={`tool-toggle-btn ${showComponentTools ? "active" : ""}`}
            onClick={() => setShowComponentTools(!showComponentTools)}
            title="Show/Hide Component Tools"
          >
            🔧
          </button>
        )}
      </div>

      {selected && showComponentTools && renderComponentTools()}

      <div className="layers-container">
        {rootNodeId ? (
          renderLayer(rootNodeId)
        ) : (
          <div className="no-components-message">No components available</div>
        )}
      </div>

      {!selected && (
        <div className="layers-empty-selection">
          <h4>No component selected</h4>
          <p>
            Select any text, button, image or container in the canvas to
            customize its properties.
          </p>
          <div className="empty-illustration">
            <span>👆</span>
          </div>
        </div>
      )}
    </div>
  );
};
